'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { t } from '@/i18n/translations'

/**
 * "Gallery · Field" compare ledger for the cities page: region switcher +
 * sortable columns (city / region / when-to-go) over the country's cities.
 * Mirrors the reference #compare section and the eSIM plans ledger structure.
 *
 * `rows` is [{ id, name, href, region, regionValue, season, seasonSort }].
 * `regions` is [{ value, label }] (only regions that have cities).
 */
export default function GfCompareLedger({ rows = [], regions = [], lang = 'en', countryName = '', region: regionProp, onRegionChange }) {
    const ui = t(lang, 'serviceWidgetsUi').compare
    // Controlled (parent owns region — e.g. region cards select it) or self-managed.
    const controlled = typeof onRegionChange === 'function'
    const [regionState, setRegionState] = useState('all')
    const region = controlled ? (regionProp || 'all') : regionState
    const setRegion = (v) => { if (controlled) onRegionChange(v); else setRegionState(v) }
    const [sortKey, setSortKey] = useState('season')
    const [sortDir, setSortDir] = useState(1)

    const list = useMemo(() => {
        const filtered = rows.filter((r) => region === 'all' || r.regionValue === region)
        const k = sortKey
        const val = (r) => (k === 'city' ? r.name : k === 'region' ? r.region : r.seasonSort)
        return filtered.slice().sort((a, b) => {
            const x = val(a)
            const y = val(b)
            if (x === y) return a.name < b.name ? -1 : 1
            if (typeof x === 'string') return x < y ? -sortDir : sortDir
            return (x < y ? -1 : 1) * sortDir
        })
    }, [rows, region, sortKey, sortDir])

    const toggleSort = (k) => {
        if (sortKey === k) setSortDir((d) => d * -1)
        else { setSortKey(k); setSortDir(1) }
    }

    const Head = ({ k, label }) => (
        <span data-sort={k} className={sortKey === k ? 'act' : ''} onClick={() => toggleSort(k)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(k) } }}>
            {label}<i className="ar">{sortKey === k ? (sortDir > 0 ? '▲' : '▼') : ''}</i>
        </span>
    )

    const regionLabel = region === 'all'
        ? ui.allRegions(countryName)
        : (regions.find((r) => r.value === region)?.label || region)

    return (
        <>
            {regions.length > 1 && (
                <div className="gf-ops" role="group" aria-label={ui.regionAria}>
                    <button type="button" className={`all ${region === 'all' ? 'on' : ''}`} onClick={() => setRegion('all')}>{ui.all}</button>
                    {regions.map((r) => (
                        <button type="button" key={r.value} className={region === r.value ? 'on' : ''} onClick={() => setRegion(r.value)}>{r.label}</button>
                    ))}
                </div>
            )}

            <div className="gf-plan-head">
                <span className="gf-plan-count">{ui.cityCount(list.length)}</span>
                <span className="gf-note">{regionLabel}</span>
            </div>

            <div className="gf-plans gf-plans-cities">
                <div className="gf-plan-row head">
                    <Head k="city" label={ui.city} />
                    <Head k="region" label={ui.region} />
                    <Head k="season" label={ui.when} />
                    {/* invisible clone of the row's guide link so the header's last
                        (auto) track reserves the same width as data rows — keeps the
                        column headers aligned over their columns */}
                    <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{ui.guide}</span>
                </div>
                {list.map((r) => (
                    <div className="gf-plan-row" key={r.id}>
                        <span className="d"><span className="gbline">{r.name}</span></span>
                        <span className="v">{r.region}</span>
                        <span className="per">{r.season}</span>
                        <Link className="sel" href={r.href}>{ui.guide}</Link>
                    </div>
                ))}
            </div>
        </>
    )
}
